import {
  ModuleId,
  Question,
  QuestionCore,
  QuestionState,
  SubSkillProgress,
} from '../types'
import {
  getQuestionWithExplanation,
  getModuleQuestions,
  getModuleSubSkillQuestions,
  QUESTIONS_BY_FAMILY,
  getQuestionsForTier,
} from '../data/questionLoader'
import { GroupId } from '../config/systemConfig'
import {
  TIERS,
  TierConfig,
  getTierConfig,
  getTiersForGroup,
  getAllTiers,
  getAllowedTiersForGroup,
  isTierAllowedForGroup,
  clampTierToGroup,
  getTierRangeForGroup,
} from '../config/tiers'
import { createSessionRng, createRng, SeededRng } from './deterministicRng'
import { FamilyMasteryState, getFamilyMasteryKey } from './familyMasteryEngine'
import {
  getFamiliesForModule,
  FamilyDefinition,
} from '../config/taxonomyRegistry'
import { getQuestionStateKey } from './questionAttemptHistory'
import {
  debug as logDebug,
  warn as logWarn,
  error as logError,
  group,
  groupEnd,
} from './logger'
import { buildLDG, calculateFrontier } from '../governance/ldg/ldgBuilder'
import { ALL_PREREQUISITES } from '../governance/ldg/prerequisites'
import type { LDGraph } from '../governance/ldg/types'
import { computeDecayedMastery } from './masteryDecay'

// ─── LDG Singleton (built once, shared) ───────────────────────────────────────
let _ldgGraph: LDGraph | null = null

function getLDG(): LDGraph {
  if (!_ldgGraph) {
    _ldgGraph = buildLDG(ALL_PREREQUISITES)
    debugLog(
      `📊 LDG built: ${_ldgGraph.metadata.nodeCount} nodes, ${_ldgGraph.metadata.edgeCount} edges, valid=${_ldgGraph.metadata.isValidDAG}`,
    )
  }
  return _ldgGraph
}

/**
 * Check if a family's hard prerequisites are met based on mastery state.
 * Returns true if all hard prerequisites have mastery >= threshold.
 *
 * RESOLVES: CONFLICT-01 (LDG Exists But Is Not Enforced)
 */
function arePrerequisitesMet(
  familyId: string,
  familyMastery: Record<string, FamilyMasteryState>,
  selectedGroup: GroupId,
  threshold: number = V11_CONFIG.STRUGGLING_THRESHOLD,
): boolean {
  const graph = getLDG()
  const node = graph.nodes.get(familyId)
  if (!node) return true // Unknown node — allow (don't block)

  // No prerequisites — always allowed
  if (node.prerequisites.length === 0) return true

  // Check each hard prerequisite
  for (const prereqId of node.prerequisites) {
    const edge = graph.edges.find(
      (e) => e.from === prereqId && e.to === familyId,
    )
    // Only enforce hard prerequisites (soft are recommendations)
    if (edge && edge.type === 'soft') continue

    const masteryKey = getFamilyMasteryKey(prereqId, selectedGroup)
    const prereqState = familyMastery[masteryKey]

    // Apply decay to prerequisite mastery for accurate check
    const effectiveMastery = computeDecayedMastery(prereqState)

    if (effectiveMastery < threshold) {
      debugLog(
        `   🔒 Prerequisite not met: ${prereqId} (mastery: ${effectiveMastery}, threshold: ${threshold}) blocks ${familyId}`,
      )
      return false
    }
  }

  return true
}

/**
 * ADAPTIVE ENGINE — FamilyId-First Selection with Group/Tier Gating
 *
 * Modern tier-based adaptive selection:
 * - Enforces selectedGroup → allowedTiers constraint at EVERY selection step
 * - Selects questions based on the WEAKEST familyId within a module
 * - Uses FamilyMasteryState (ELO-based) as input
 * - Operates purely on TIERS (1-16)
 * - ALL fallbacks are constrained to allowed tiers for the group
 *
 * Selection flow:
 * 1. Get allowed tiers for selectedGroup
 * 2. Get all familyIds for module, FILTER to allowed tiers only
 * 3. Score each family by priority (weakest first)
 * 4. Pick top-priority family (with seeded randomness)
 * 5. Determine target tier from mastery, CLAMP to allowed range
 * 6. Find questions matching familyId + tier range (within allowed)
 * 7. Fallback: wider tier → adjacent family → any allowed tier
 * 8. NEVER select outside allowed tiers — return null instead
 */

// Debug flag — enabled in development, disabled in production
const ADAPTIVE_DEBUG = process.env.NODE_ENV === 'development'
function debugLog(...args: any[]) {
  if (ADAPTIVE_DEBUG) logDebug(...args)
}

// v11.2 Configuration - Hardened Pedagogical Rules
const V11_CONFIG = {
  /** Mastery score below which a family is considered "struggling" */
  STRUGGLING_THRESHOLD: 40,
  /** Mastery score below which a family is considered "learning" */
  LEARNING_THRESHOLD: 70,
  /** Mastery score above which a family is considered "mastered" */
  MASTERED_THRESHOLD: 85,
  /** Minimum attempts before mastery is considered stable */
  MASTERY_CONFIDENCE_MIN_ATTEMPTS: 5,
  /** Maximum number of mastered-family questions per session (soft cap) */
  MAX_MASTERED_PER_SESSION: 2,
  /** Maximum percentage of session that can be mastered content (70%) */
  MAX_MASTERED_PERCENTAGE: 0.7,
  /** Maximum questions from same family per session */
  MAX_QUESTIONS_PER_FAMILY: 2,
  /** Minimum different families per session (if available) */
  MIN_FAMILIES_PER_SESSION: 2,
  /** Recency penalty: reduce probability by 50% if used in last session */
  RECENCY_PENALTY_MULTIPLIER: 0.5,
  /** Tier window: how many tiers below target to include */
  TIER_WINDOW_BELOW: 1,
  /** Tier window: how many tiers above target to include */
  TIER_WINDOW_ABOVE: 1,
  /** Expanded tier window for fallback attempts */
  TIER_WINDOW_FALLBACK: 3,
  /** Number of top-priority families to randomly pick from */
  TOP_CANDIDATE_POOL: 4,
  /** Cooldown: recently-seen familyIds to deprioritize */
  RECENT_FAMILY_COOLDOWN: 5,
  /** Max retries per slot */
  MAX_RETRIES_PER_SLOT: 5,
  /** Max total retries across session */
  MAX_TOTAL_RETRIES: 50,
  /** Min tier value */
  MIN_TIER: 1,
  /** Max tier value (16-tier system) */
  MAX_TIER: 16,
  /** Level unlock threshold (ratio of mastered/practiced questions) */
  LEVEL_UNLOCK_THRESHOLD: 0.5,
  /** Minimum questions attempted at a level before unlock is possible */
  LEVEL_UNLOCK_MIN_ATTEMPTED: 1,
}

/**
 * adaptiveEngine.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Canonical mastery thresholds — aligned with masteryCanonical.ts and
 * dashboardStats.ts (previously diverged: was 0.8/0.6, now unified).
 */

// ─── Canonical thresholds (must match dashboardStats.ts MASTERY_THRESHOLDS) ──
export const ADAPTIVE_MASTERY_THRESHOLD = 0.8 // was 0.8 ✓ (now explicit)
export const ADAPTIVE_PROFICIENT_THRESHOLD = 0.6 // was 0.6 ✓ (now explicit)
export const ADAPTIVE_DEVELOPING_THRESHOLD = 0.4 // NEW — aligns with dashboardStats
export const ADAPTIVE_STRUGGLING_THRESHOLD = 0.4 // was 0.6 (was too high — fixed)

/**
 * Compute mastery confidence based on attempts and consistency.
 * Returns 0-1 score indicating how reliable the mastery score is.
 */
function computeMasteryConfidence(
  masteryState: FamilyMasteryState | undefined,
): number {
  if (!masteryState || masteryState.attempts === 0) return 0

  // Base confidence from attempt count
  const attemptConfidence = Math.min(
    1,
    masteryState.attempts / V11_CONFIG.MASTERY_CONFIDENCE_MIN_ATTEMPTS,
  )

  // Combine with built-in confidence metric
  const combinedConfidence = (attemptConfidence + masteryState.confidence) / 2
  return combinedConfidence
}

/**
 * Check if a family's mastery is truly stable (not just high score with low attempts).
 */
function isMasteryStable(
  masteryState: FamilyMasteryState | undefined,
): boolean {
  if (!masteryState) return false
  if (masteryState.masteryScore < V11_CONFIG.MASTERED_THRESHOLD) return false
  if (masteryState.attempts < V11_CONFIG.MASTERY_CONFIDENCE_MIN_ATTEMPTS)
    return false
  const confidence = computeMasteryConfidence(masteryState)
  return confidence >= 0.6 // Require 60% confidence for stable mastery
}

/**
 * Compute a priority score for a family based on its mastery state.
 * Higher score = higher priority (more likely to be selected).
 *
 * v11.2: Enhanced with recency weighting and confidence checks.
 *
 * NOTE: This function now receives mastery state directly (already group-scoped by caller).
 */
function computeFamilyPriority(
  family: FamilyDefinition,
  masteryState: FamilyMasteryState | undefined,
  recentFamilyIds: Set<string>,
): number {
  let score: number
  if (!masteryState || masteryState.attempts === 0) {
    // Never attempted — highest priority (explore new content)
    score = 100
  } else if (masteryState.masteryScore < V11_CONFIG.STRUGGLING_THRESHOLD) {
    score = 90
  } else if (masteryState.masteryScore < V11_CONFIG.LEARNING_THRESHOLD) {
    score = 70
  } else if (masteryState.masteryScore < V11_CONFIG.MASTERED_THRESHOLD) {
    score = 40
  } else {
    // Mastered, but check if it's stable
    const isStable = isMasteryStable(masteryState)
    score = isStable ? 10 : 30 // Unstable mastery gets higher priority
  }

  // v11.2: ENHANCED recency penalty (50% reduction)
  if (recentFamilyIds.has(family.familyId)) {
    score = score * V11_CONFIG.RECENCY_PENALTY_MULTIPLIER
    debugLog(
      `   🕐 Recency penalty applied to ${family.familyId}: ${score * 2} → ${score}`,
    )
  }

  // Confidence penalty: low confidence means the score is unreliable,
  // so we should test this family more
  const confidence = computeMasteryConfidence(masteryState)
  if (masteryState && confidence < 0.3 && masteryState.attempts > 0) {
    score += 10
    debugLog(
      `   📊 Low confidence bonus for ${family.familyId}: +10 (confidence: ${confidence.toFixed(2)})`,
    )
  }
  return Math.max(0, score)
}

/**
 * Determine the target difficulty tier for a family based on its mastery.
 * The tier is clamped to the allowed range for the selectedGroup.
 *
 * Strategy:
 * - mastery < 40 → family's base tier (practice fundamentals)
 * - mastery 40-70 → base tier + 0 to +1 (consolidate)
 * - mastery > 70 → base tier + 1 to +2 (challenge)
 */
function determineTargetTier(
  family: FamilyDefinition,
  masteryState: FamilyMasteryState | undefined,
  allowedTiers: Set<number>,
): number {
  const baseTier = family.tier
  let targetTier: number
  if (!masteryState || masteryState.attempts === 0) {
    // New family: start at base tier
    targetTier = baseTier
  } else {
    const mastery = masteryState.masteryScore
    if (mastery < V11_CONFIG.STRUGGLING_THRESHOLD) {
      targetTier = baseTier
    } else if (mastery < V11_CONFIG.LEARNING_THRESHOLD) {
      targetTier = baseTier + (mastery >= 55 ? 1 : 0)
    } else if (mastery < V11_CONFIG.MASTERED_THRESHOLD) {
      targetTier = baseTier + 1
    } else {
      targetTier = baseTier + 2
    }
  }

  // Clamp to allowed tiers for the group (NOT global 1-16)
  const allowedArray = Array.from(allowedTiers).sort((a, b) => a - b)
  if (allowedArray.length === 0) return baseTier
  const minAllowed = allowedArray[0]
  const maxAllowed = allowedArray[allowedArray.length - 1]
  const clamped = Math.max(minAllowed, Math.min(maxAllowed, targetTier))
  if (clamped !== targetTier) {
    debugLog(
      `🔒 v11.2: Clamped targetTier ${targetTier} → ${clamped} (allowed: ${minAllowed}-${maxAllowed}) for family ${family.familyId}`,
    )
  }
  return clamped
}

/**
 * Build a set of difficulty tiers from a tier range,
 * INTERSECTED with allowed tiers for the group.
 */
function buildTierWindow(
  centerTier: number,
  below: number,
  above: number,
  allowedTiers: Set<number>,
): Set<number> {
  const window = new Set<number>()
  const minTier = centerTier - below
  const maxTier = centerTier + above
  for (let tier = minTier; tier <= maxTier; tier++) {
    // Only include tiers that are in the allowed set
    if (allowedTiers.has(tier)) {
      window.add(tier)
    }
  }
  return window
}

/**
 * Find candidate questions for a specific familyId within a tier window.
 * Uses the QUESTIONS_BY_FAMILY index for O(1) lookup.
 */
function findQuestionsForFamily(
  familyId: string,
  tierWindow: Set<number>,
  excludeQuestionIds: Set<string>,
): QuestionCore[] {
  const familyQuestions = QUESTIONS_BY_FAMILY.get(familyId) || []
  return familyQuestions.filter((q) => {
    // Must be within tier window
    if (!tierWindow.has(q.tier)) return false
    // Must not already be in session
    if (excludeQuestionIds.has(q.id)) return false
    return true
  })
}

/**
 * Select a single question using the v11.2 familyId-first algorithm
 * with strict pedagogical rules and group/tier gating.
 *
 * v11.2 Enhancements:
 * - Per-family question cap (max 2 per family)
 * - Recency-based question exclusion
 * - Mastery confidence checks
 * - Controlled reinforcement mode
 *
 * GROUP-SCOPED MASTERY: This function now uses group-scoped mastery keys internally.
 */
function selectAdaptiveQuestionV11(
  moduleId: ModuleId,
  familyMastery: Record<string, FamilyMasteryState>,
  selectedGroup: GroupId,
  allowedTiers: Set<number>,
  sessionFamilyIds: Set<string>,
  sessionQuestionIds: Set<string>,
  recentFamilyIds: Set<string>,
  recentQuestionIds: Set<string>,
  masteredCount: number,
  familyQuestionCounts: Map<string, number>,
  sessionSize: number,
  rng: SeededRng,
): {
  question: Question | null
  reason: string
  fallbackLevel: number
} {
  // 1. Get all families for this module from taxonomy
  const allModuleFamilies = getFamiliesForModule(moduleId)
  if (allModuleFamilies.length === 0) {
    const reason = `No families found in taxonomy for module ${moduleId}`
    debugLog(`⚠️ v11.2: ${reason}`)
    return {
      question: null,
      reason,
      fallbackLevel: 0,
    }
  }

  // 2. CRITICAL: Filter families to only those whose tier is within allowed tiers
  const eligibleFamilies = allModuleFamilies.filter((f) =>
    allowedTiers.has(f.tier),
  )

  // 2b. NEW: Filter by LDG prerequisites (CONFLICT-01 resolution)
  const prerequisiteReadyFamilies = eligibleFamilies.filter((f) =>
    arePrerequisitesMet(f.familyId, familyMastery, selectedGroup),
  )

  // Use prerequisite-ready families as primary pool, fall back to all eligible if none ready
  const selectionPool =
    prerequisiteReadyFamilies.length > 0
      ? prerequisiteReadyFamilies
      : eligibleFamilies

  if (prerequisiteReadyFamilies.length < eligibleFamilies.length) {
    debugLog(
      `🔒 LDG Gating: ${eligibleFamilies.length - prerequisiteReadyFamilies.length} families locked by prerequisites (${prerequisiteReadyFamilies.length}/${eligibleFamilies.length} ready)`,
    )
  }

  if (selectionPool.length === 0) {
    const reason = `No families for module ${moduleId} within allowed tiers [${Array.from(allowedTiers).join(',')}] for group ${selectedGroup}`
    debugLog(`⚠️ v11.2: ${reason}`)
    return {
      question: null,
      reason,
      fallbackLevel: 0,
    }
  }
  debugLog(
    `📋 v11.2: Module ${moduleId}, Group ${selectedGroup}, Allowed tiers: [${Array.from(
      allowedTiers,
    )
      .sort((a, b) => a - b)
      .join(
        ',',
      )}], Eligible families: ${selectionPool.length}/${allModuleFamilies.length} (${prerequisiteReadyFamilies.length} prereq-ready)`,
  )

  // v11.2: Calculate mastered cap based on session size (70% max)
  const maxMasteredAllowed = Math.ceil(
    sessionSize * V11_CONFIG.MAX_MASTERED_PERCENTAGE,
  )

  // 3. Score and sort families by priority
  // CRITICAL: Use group-scoped mastery keys
  const scoredFamilies = selectionPool
    .filter((f) => {
      // v11.2: Exclude families that hit per-family cap
      const usageCount = familyQuestionCounts.get(f.familyId) || 0
      if (usageCount >= V11_CONFIG.MAX_QUESTIONS_PER_FAMILY) {
        debugLog(
          `   ⚠️ Family ${f.familyId} hit per-family cap (${usageCount}/${V11_CONFIG.MAX_QUESTIONS_PER_FAMILY})`,
        )
        return false
      }
      return true
    })
    .filter((f) => !sessionFamilyIds.has(f.familyId)) // Exclude already-used families (for diversity)
    .map((family) => {
      // CRITICAL: Look up mastery using group-scoped key
      const masteryKey = getFamilyMasteryKey(family.familyId, selectedGroup)
      const masteryState = familyMastery[masteryKey]
      // Apply decay to mastery for accurate priority scoring
      const decayedScore = computeDecayedMastery(masteryState)
      const effectiveState = masteryState
        ? { ...masteryState, masteryScore: decayedScore }
        : masteryState
      return {
        family,
        mastery: effectiveState,
        priority: computeFamilyPriority(
          family,
          effectiveState,
          recentFamilyIds,
        ),
        confidence: computeMasteryConfidence(effectiveState),
      }
    })
    .filter((sf) => {
      // v11.2: Enforce mastered cap with percentage-based limit
      if (masteredCount >= maxMasteredAllowed) {
        const mastery = sf.mastery?.masteryScore ?? 0
        const isStable = isMasteryStable(sf.mastery)
        if (mastery >= V11_CONFIG.MASTERED_THRESHOLD && isStable) {
          return false // Exclude stable mastered families when at cap
        }
      }
      return true
    })
    .sort((a, b) => b.priority - a.priority)

  // NEW: Debug logging for filtering steps
  debugLog(`🔍 v11.2 Filter Chain:
    - Total eligible families: ${eligibleFamilies.length}
    - After per-family cap: ${eligibleFamilies.filter((f) => (familyQuestionCounts.get(f.familyId) || 0) < V11_CONFIG.MAX_QUESTIONS_PER_FAMILY).length}
    - After session exclusion: ${eligibleFamilies.filter((f) => !sessionFamilyIds.has(f.familyId) && (familyQuestionCounts.get(f.familyId) || 0) < V11_CONFIG.MAX_QUESTIONS_PER_FAMILY).length}
    - After mastered cap (${masteredCount}/${maxMasteredAllowed}): ${scoredFamilies.length}
    - Mastered families in pool: ${scoredFamilies.filter((sf) => (sf.mastery?.masteryScore ?? 0) >= V11_CONFIG.MASTERED_THRESHOLD).length}`)
  if (scoredFamilies.length === 0) {
    // v11.2: FALLBACK LEVEL 1 - Allow reusing families (but respect per-family cap)
    debugLog(
      `⚠️ v11.2: No new families available. FALLBACK LEVEL 1: Reusing families (respecting per-family cap)...`,
    )
    const reusableFamilies = eligibleFamilies
      .filter((f) => {
        const usageCount = familyQuestionCounts.get(f.familyId) || 0
        return usageCount < V11_CONFIG.MAX_QUESTIONS_PER_FAMILY
      })
      .map((family) => {
        // CRITICAL: Look up mastery using group-scoped key
        const masteryKey = getFamilyMasteryKey(family.familyId, selectedGroup)
        const masteryState = familyMastery[masteryKey]
        return {
          family,
          mastery: masteryState,
          priority: computeFamilyPriority(
            family,
            masteryState,
            recentFamilyIds,
          ),
          confidence: computeMasteryConfidence(masteryState),
        }
      })
      .filter((sf) => {
        // Still respect mastered cap
        if (masteredCount >= maxMasteredAllowed) {
          const mastery = sf.mastery?.masteryScore ?? 0
          const isStable = isMasteryStable(sf.mastery)
          if (mastery >= V11_CONFIG.MASTERED_THRESHOLD && isStable) {
            return false
          }
        }
        return true
      })
      .sort((a, b) => b.priority - a.priority)
    if (reusableFamilies.length > 0) {
      debugLog(
        `✅ v11.2: Found ${reusableFamilies.length} reusable families (FALLBACK LEVEL 1)`,
      )
      const poolSize = Math.min(
        V11_CONFIG.TOP_CANDIDATE_POOL,
        reusableFamilies.length,
      )
      const topCandidates = reusableFamilies.slice(0, poolSize)
      const selected = rng.pick(topCandidates)!
      const { family, mastery: masteryState } = selected
      debugLog(
        `🎯 v11.2: Selected family ${family.familyId} (FALLBACK LEVEL 1, tier: ${family.tier}, mastery: ${masteryState?.masteryScore ?? 0}, confidence: ${selected.confidence.toFixed(2)})`,
      )
      const targetTier = determineTargetTier(family, masteryState, allowedTiers)
      const tierWindow = buildTierWindow(
        targetTier,
        V11_CONFIG.TIER_WINDOW_BELOW,
        V11_CONFIG.TIER_WINDOW_ABOVE,
        allowedTiers,
      )
      let candidates = findQuestionsForFamily(
        family.familyId,
        tierWindow,
        sessionQuestionIds,
      )

      // v11.2: Exclude recent questions unless no alternatives
      const nonRecentCandidates = candidates.filter(
        (q) => !recentQuestionIds.has(q.id),
      )
      if (nonRecentCandidates.length > 0) {
        candidates = nonRecentCandidates
        debugLog(
          `   🕐 Excluded ${candidates.length - nonRecentCandidates.length} recent questions`,
        )
      }
      if (candidates.length === 0) {
        const expandedWindow = buildTierWindow(
          targetTier,
          V11_CONFIG.TIER_WINDOW_FALLBACK,
          V11_CONFIG.TIER_WINDOW_FALLBACK,
          allowedTiers,
        )
        candidates = findQuestionsForFamily(
          family.familyId,
          expandedWindow,
          sessionQuestionIds,
        )
        const nonRecentExpanded = candidates.filter(
          (q) => !recentQuestionIds.has(q.id),
        )
        if (nonRecentExpanded.length > 0) candidates = nonRecentExpanded
      }
      if (candidates.length === 0) {
        candidates = findQuestionsForFamily(
          family.familyId,
          allowedTiers,
          sessionQuestionIds,
        )
        const nonRecentAll = candidates.filter(
          (q) => !recentQuestionIds.has(q.id),
        )
        if (nonRecentAll.length > 0) candidates = nonRecentAll
      }
      if (candidates.length > 0) {
        const shuffled = rng.shuffle(candidates)
        const picked = shuffled[0]
        debugLog(
          `✅ v11.2: Picked question ${picked.id} (family: ${picked.familyId}, tier: ${picked.tier}) - FALLBACK LEVEL 1`,
        )
        return {
          question: getQuestionWithExplanation(picked.id),
          reason: 'family reuse (respecting per-family cap)',
          fallbackLevel: 1,
        }
      }
    }

    // v11.2: FALLBACK LEVEL 2 - Controlled reinforcement (mastered families)
    debugLog(
      `⚠️ v11.2: FALLBACK LEVEL 2: Controlled reinforcement (mastered families)...`,
    )
    const masteredFamilies = eligibleFamilies
      .filter((f) => {
        const usageCount = familyQuestionCounts.get(f.familyId) || 0
        return usageCount < V11_CONFIG.MAX_QUESTIONS_PER_FAMILY
      })
      .map((family) => {
        // CRITICAL: Look up mastery using group-scoped key
        const masteryKey = getFamilyMasteryKey(family.familyId, selectedGroup)
        const masteryState = familyMastery[masteryKey]
        return {
          family,
          mastery: masteryState,
          priority: computeFamilyPriority(
            family,
            masteryState,
            recentFamilyIds,
          ),
          confidence: computeMasteryConfidence(masteryState),
        }
      })
      .filter((sf) => {
        const mastery = sf.mastery?.masteryScore ?? 0
        const isStable = isMasteryStable(sf.mastery)
        return mastery >= V11_CONFIG.MASTERED_THRESHOLD && isStable
      })
      .sort((a, b) => b.priority - a.priority)
    if (masteredFamilies.length > 0) {
      debugLog(
        `✅ v11.2: Found ${masteredFamilies.length} mastered families for reinforcement (FALLBACK LEVEL 2)`,
      )
      const poolSize = Math.min(
        V11_CONFIG.TOP_CANDIDATE_POOL,
        masteredFamilies.length,
      )
      const topCandidates = masteredFamilies.slice(0, poolSize)
      const selected = rng.pick(topCandidates)!
      const { family, mastery: masteryState } = selected
      debugLog(
        `🎯 v11.2: Selected MASTERED family for reinforcement: ${family.familyId} (tier: ${family.tier}, mastery: ${masteryState?.masteryScore ?? 0}, confidence: ${selected.confidence.toFixed(2)})`,
      )
      const targetTier = determineTargetTier(family, masteryState, allowedTiers)
      const tierWindow = buildTierWindow(
        targetTier,
        V11_CONFIG.TIER_WINDOW_BELOW,
        V11_CONFIG.TIER_WINDOW_ABOVE,
        allowedTiers,
      )
      let candidates = findQuestionsForFamily(
        family.familyId,
        tierWindow,
        sessionQuestionIds,
      )
      const nonRecentCandidates = candidates.filter(
        (q) => !recentQuestionIds.has(q.id),
      )
      if (nonRecentCandidates.length > 0) candidates = nonRecentCandidates
      if (candidates.length === 0) {
        const expandedWindow = buildTierWindow(
          targetTier,
          V11_CONFIG.TIER_WINDOW_FALLBACK,
          V11_CONFIG.TIER_WINDOW_FALLBACK,
          allowedTiers,
        )
        candidates = findQuestionsForFamily(
          family.familyId,
          expandedWindow,
          sessionQuestionIds,
        )
        const nonRecentExpanded = candidates.filter(
          (q) => !recentQuestionIds.has(q.id),
        )
        if (nonRecentExpanded.length > 0) candidates = nonRecentExpanded
      }
      if (candidates.length === 0) {
        candidates = findQuestionsForFamily(
          family.familyId,
          allowedTiers,
          sessionQuestionIds,
        )
        const nonRecentAll = candidates.filter(
          (q) => !recentQuestionIds.has(q.id),
        )
        if (nonRecentAll.length > 0) candidates = nonRecentAll
      }
      if (candidates.length > 0) {
        const shuffled = rng.shuffle(candidates)
        const picked = shuffled[0]
        debugLog(
          `✅ v11.2: Picked reinforcement question ${picked.id} (family: ${picked.familyId}, tier: ${picked.tier})`,
        )
        return {
          question: getQuestionWithExplanation(picked.id),
          reason: 'reinforcement (controlled, stable mastery)',
          fallbackLevel: 2,
        }
      }
    }

    // v11.2: FALLBACK LEVEL 3 - Emergency (any family, ignore all caps)
    debugLog(`⚠️ v11.2: FALLBACK LEVEL 3: Emergency fallback (ignore caps)...`)
    const anyFamilies = eligibleFamilies
      .map((family) => {
        // CRITICAL: Look up mastery using group-scoped key
        const masteryKey = getFamilyMasteryKey(family.familyId, selectedGroup)
        const masteryState = familyMastery[masteryKey]
        return {
          family,
          mastery: masteryState,
          priority: computeFamilyPriority(
            family,
            masteryState,
            recentFamilyIds,
          ),
          confidence: computeMasteryConfidence(masteryState),
        }
      })
      .sort((a, b) => b.priority - a.priority)
    if (anyFamilies.length > 0) {
      debugLog(
        `✅ v11.2: Found ${anyFamilies.length} families (FALLBACK LEVEL 3 - emergency)`,
      )
      const poolSize = Math.min(
        V11_CONFIG.TOP_CANDIDATE_POOL,
        anyFamilies.length,
      )
      const topCandidates = anyFamilies.slice(0, poolSize)
      const selected = rng.pick(topCandidates)!
      const { family, mastery: masteryState } = selected
      debugLog(
        `🎯 v11.2: Selected ANY family (FALLBACK LEVEL 3): ${family.familyId} (tier: ${family.tier}, mastery: ${masteryState?.masteryScore ?? 0})`,
      )
      const targetTier = determineTargetTier(family, masteryState, allowedTiers)
      const tierWindow = buildTierWindow(
        targetTier,
        V11_CONFIG.TIER_WINDOW_BELOW,
        V11_CONFIG.TIER_WINDOW_ABOVE,
        allowedTiers,
      )
      let candidates = findQuestionsForFamily(
        family.familyId,
        tierWindow,
        sessionQuestionIds,
      )
      const nonRecentCandidates = candidates.filter(
        (q) => !recentQuestionIds.has(q.id),
      )
      if (nonRecentCandidates.length > 0) candidates = nonRecentCandidates
      if (candidates.length === 0) {
        const expandedWindow = buildTierWindow(
          targetTier,
          V11_CONFIG.TIER_WINDOW_FALLBACK,
          V11_CONFIG.TIER_WINDOW_FALLBACK,
          allowedTiers,
        )
        candidates = findQuestionsForFamily(
          family.familyId,
          expandedWindow,
          sessionQuestionIds,
        )
        const nonRecentExpanded = candidates.filter(
          (q) => !recentQuestionIds.has(q.id),
        )
        if (nonRecentExpanded.length > 0) candidates = nonRecentExpanded
      }
      if (candidates.length === 0) {
        candidates = findQuestionsForFamily(
          family.familyId,
          allowedTiers,
          sessionQuestionIds,
        )
        // Allow recent questions in emergency
      }
      if (candidates.length > 0) {
        const shuffled = rng.shuffle(candidates)
        const picked = shuffled[0]
        debugLog(
          `✅ v11.2: Picked emergency question ${picked.id} (family: ${picked.familyId}, tier: ${picked.tier})`,
        )
        return {
          question: getQuestionWithExplanation(picked.id),
          reason: 'emergency fallback (all caps ignored)',
          fallbackLevel: 3,
        }
      }
    }
    const reason = `No questions available for ${moduleId} in group ${selectedGroup} (allowed tiers: [${Array.from(allowedTiers).join(',')}]) after all fallbacks`
    debugLog(`❌ v11.2: ${reason}`)
    return {
      question: null,
      reason,
      fallbackLevel: 4,
    }
  }

  // 4. Pick from top candidates (with seeded randomness for variety)
  const poolSize = Math.min(
    V11_CONFIG.TOP_CANDIDATE_POOL,
    scoredFamilies.length,
  )
  const topCandidates = scoredFamilies.slice(0, poolSize)
  const selected = rng.pick(topCandidates)!
  const { family, mastery: masteryState, confidence } = selected
  debugLog(
    `🎯 v11.2: Selected family ${family.familyId} (tier: ${family.tier}, mastery: ${masteryState?.masteryScore ?? 0}, priority: ${selected.priority}, confidence: ${confidence.toFixed(2)})`,
  )

  // 5. Determine target tier (clamped to allowed tiers)
  const targetTier = determineTargetTier(family, masteryState, allowedTiers)

  // 6. Build tier window and find questions (intersected with allowed tiers)
  const tierWindow = buildTierWindow(
    targetTier,
    V11_CONFIG.TIER_WINDOW_BELOW,
    V11_CONFIG.TIER_WINDOW_ABOVE,
    allowedTiers,
  )
  let candidates = findQuestionsForFamily(
    family.familyId,
    tierWindow,
    sessionQuestionIds,
  )

  // v11.2: Exclude recent questions unless no alternatives
  const nonRecentCandidates = candidates.filter(
    (q) => !recentQuestionIds.has(q.id),
  )
  if (nonRecentCandidates.length > 0) {
    candidates = nonRecentCandidates
    debugLog(
      `   🕐 Excluded ${candidates.length - nonRecentCandidates.length} recent questions`,
    )
  }
  let fallbackReason = ''

  // 7. Fallback: expand tier window (still within allowed tiers)
  if (candidates.length === 0) {
    fallbackReason = `expanded window (±${V11_CONFIG.TIER_WINDOW_FALLBACK})`
    debugLog(
      `⚠️ v11.2: No questions for ${family.familyId} at tier ${targetTier} (±1). Expanding window within allowed tiers...`,
    )
    const expandedWindow = buildTierWindow(
      targetTier,
      V11_CONFIG.TIER_WINDOW_FALLBACK,
      V11_CONFIG.TIER_WINDOW_FALLBACK,
      allowedTiers,
    )
    candidates = findQuestionsForFamily(
      family.familyId,
      expandedWindow,
      sessionQuestionIds,
    )
    const nonRecentExpanded = candidates.filter(
      (q) => !recentQuestionIds.has(q.id),
    )
    if (nonRecentExpanded.length > 0) candidates = nonRecentExpanded
  }

  // 8. Fallback: try ANY allowed tier for this family (but NEVER outside group)
  if (candidates.length === 0) {
    fallbackReason = 'any allowed tier'
    debugLog(
      `⚠️ v11.2: No questions for ${family.familyId} in expanded window. Trying any ALLOWED tier...`,
    )
    candidates = findQuestionsForFamily(
      family.familyId,
      allowedTiers,
      sessionQuestionIds,
    )
    const nonRecentAll = candidates.filter((q) => !recentQuestionIds.has(q.id))
    if (nonRecentAll.length > 0) candidates = nonRecentAll
  }

  // 9. Fallback: try adjacent families (same skill, within allowed tiers)
  if (candidates.length === 0) {
    fallbackReason = 'adjacent family'
    debugLog(
      `⚠️ v11.2: No questions at all for ${family.familyId} within allowed tiers. Trying adjacent families...`,
    )
    const adjacentFamilies = eligibleFamilies.filter(
      (f) =>
        f.familyId !== family.familyId &&
        f.skill === family.skill &&
        (familyQuestionCounts.get(f.familyId) || 0) <
          V11_CONFIG.MAX_QUESTIONS_PER_FAMILY,
    )
    for (const adjFamily of adjacentFamilies) {
      const adjWindow = buildTierWindow(
        adjFamily.tier,
        V11_CONFIG.TIER_WINDOW_BELOW,
        V11_CONFIG.TIER_WINDOW_ABOVE,
        allowedTiers,
      )
      candidates = findQuestionsForFamily(
        adjFamily.familyId,
        adjWindow,
        sessionQuestionIds,
      )
      const nonRecentAdj = candidates.filter(
        (q) => !recentQuestionIds.has(q.id),
      )
      if (nonRecentAdj.length > 0) {
        candidates = nonRecentAdj
        debugLog(
          `✅ v11.2: Found question via adjacent family ${adjFamily.familyId} (tier ${adjFamily.tier})`,
        )
        break
      }
      candidates = findQuestionsForFamily(
        adjFamily.familyId,
        allowedTiers,
        sessionQuestionIds,
      )
      const nonRecentAdjAll = candidates.filter(
        (q) => !recentQuestionIds.has(q.id),
      )
      if (nonRecentAdjAll.length > 0) {
        candidates = nonRecentAdjAll
        debugLog(
          `✅ v11.2: Found question via adjacent family ${adjFamily.familyId} (any allowed tier)`,
        )
        break
      }
    }
  }

  // 10. Final fallback: try ANY eligible family at ANY allowed tier
  if (candidates.length === 0) {
    fallbackReason = 'any eligible family, any allowed tier'
    debugLog(
      `⚠️ v11.2: Adjacent families exhausted. Trying any eligible family at any allowed tier...`,
    )
    for (const f of eligibleFamilies) {
      const usageCount = familyQuestionCounts.get(f.familyId) || 0
      if (usageCount >= V11_CONFIG.MAX_QUESTIONS_PER_FAMILY) continue
      candidates = findQuestionsForFamily(
        f.familyId,
        allowedTiers,
        sessionQuestionIds,
      )
      const nonRecentFinal = candidates.filter(
        (q) => !recentQuestionIds.has(q.id),
      )
      if (nonRecentFinal.length > 0) {
        candidates = nonRecentFinal
        debugLog(`✅ v11.2: Found question via fallback family ${f.familyId}`)
        break
      }
    }
  }
  if (candidates.length === 0) {
    const reason = `No questions available for ${moduleId} in group ${selectedGroup} (allowed tiers: [${Array.from(allowedTiers).join(',')}]) after all fallbacks`
    debugLog(`❌ v11.2: ${reason}`)
    return {
      question: null,
      reason,
      fallbackLevel: 4,
    }
  }

  // 11. Pick a question (seeded random)
  const shuffled = rng.shuffle(candidates)
  const picked = shuffled[0]
  const reason = fallbackReason
    ? `selected via fallback: ${fallbackReason}`
    : `direct match (family: ${family.familyId}, targetTier: ${targetTier})`
  debugLog(
    `✅ v11.2: Picked ${picked.id} (family: ${picked.familyId}, tier: ${picked.tier}) — ${reason}`,
  )
  return {
    question: getQuestionWithExplanation(picked.id),
    reason,
    fallbackLevel: 0,
  }
}

/**
 * Generate a practice session using the v11.2 familyId-first algorithm
 * with strict pedagogical rules and group/tier gating.
 *
 * v11.2 Enhancements:
 * - Per-family question cap (max 2 per family)
 * - Mastery confidence checks (min 5 attempts for stable mastery)
 * - Recency-based question exclusion
 * - Controlled reinforcement (70% max mastered content)
 * - Minimum family diversity (2+ families when possible)
 * - Enhanced debug summary with pedagogical metrics
 *
 * @param moduleId - Module to practice
 * @param familyMastery - FamilyId mastery states from AppState
 * @param selectedGroup - Current school group (ENFORCED for tier gating)
 * @param questionStates - Per-question states (for mastered-count tracking)
 * @param count - Number of questions to generate
 * @param recentFamilyIds - Recently-seen familyIds (cross-session cooldown)
 * @param recentQuestionIds - Recently-seen questionIds (exclude from selection)
 * @param sessionSeed - Optional seed for deterministic generation
 * @returns Array of questions for the session
 */
export function getAdaptiveSessionV11(
  moduleId: ModuleId,
  familyMastery: Record<string, FamilyMasteryState>,
  selectedGroup: GroupId,
  questionStates: Record<string, QuestionState>,
  count: number = 10,
  recentFamilyIds: string[] = [],
  recentQuestionIds: string[] = [],
  sessionSeed?: number,
): Question[] {
  // Create seeded RNG
  const seed = sessionSeed ?? Date.now()
  const rng = createSessionRng(moduleId, selectedGroup, seed)

  // CRITICAL: Compute allowed tiers for this group ONCE
  const allowedTiers = getAllowedTiersForGroup(selectedGroup)
  const tierRange = getTierRangeForGroup(selectedGroup)
  logDebug(
    `\n🎲 v11.2 Session Generation START: module=${moduleId}, group=${selectedGroup}, seed=${rng.seed}`,
  )
  logDebug(
    `   📊 Allowed tiers: [${tierRange.min}-${tierRange.max}] (${allowedTiers.size} tiers)`,
  )

  // Pre-flight validation
  const allModuleFamilies = getFamiliesForModule(moduleId)
  logDebug(`   📋 Total families in module: ${allModuleFamilies.length}`)
  if (allModuleFamilies.length === 0) {
    logError(`v11.2: No families found in taxonomy for module ${moduleId}`)
    return []
  }
  const eligibleFamilies = allModuleFamilies.filter((f) =>
    allowedTiers.has(f.tier),
  )
  logDebug(
    `   ✅ Eligible families (within tier range): ${eligibleFamilies.length}/${allModuleFamilies.length}`,
  )
  if (eligibleFamilies.length === 0) {
    logWarn(
      `v11.2: No families for module ${moduleId} within allowed tiers [${tierRange.min}-${tierRange.max}] for group ${selectedGroup}`,
    )
    logWarn(
      `   Module has ${allModuleFamilies.length} families, but none match the group's tier range.`,
    )
    logWarn(
      `   This usually means the module content is for a different grade level.`,
    )
    return []
  }

  // Check if any eligible families have questions
  let familiesWithQuestions = 0
  let totalQuestionsAvailable = 0
  const familyQuestionCounts: Record<string, number> = {}
  eligibleFamilies.forEach((family) => {
    const questions = QUESTIONS_BY_FAMILY.get(family.familyId) || []
    const inTierQuestions = questions.filter((q) => allowedTiers.has(q.tier))
    familyQuestionCounts[family.familyId] = inTierQuestions.length
    if (inTierQuestions.length > 0) {
      familiesWithQuestions++
      totalQuestionsAvailable += inTierQuestions.length
    }
  })
  logDebug(
    `   📚 Families with questions: ${familiesWithQuestions}/${eligibleFamilies.length}`,
  )
  logDebug(`   📝 Total questions available: ${totalQuestionsAvailable}`)

  // v11.2: Enhanced mastery distribution with confidence metrics
  const masteryDistribution = {
    unstarted: 0,
    struggling: 0,
    learning: 0,
    masteredUnstable: 0,
    masteredStable: 0,
  }
  eligibleFamilies.forEach((family) => {
    // CRITICAL: Use group-scoped mastery key
    const masteryKey = getFamilyMasteryKey(family.familyId, selectedGroup)
    const mastery = familyMastery[masteryKey]
    if (!mastery || mastery.attempts === 0) {
      masteryDistribution.unstarted++
    } else if (mastery.masteryScore < V11_CONFIG.STRUGGLING_THRESHOLD) {
      masteryDistribution.struggling++
    } else if (mastery.masteryScore < V11_CONFIG.MASTERED_THRESHOLD) {
      masteryDistribution.learning++
    } else {
      const isStable = isMasteryStable(mastery)
      if (isStable) {
        masteryDistribution.masteredStable++
      } else {
        masteryDistribution.masteredUnstable++
      }
    }
  })
  logDebug(`   📊 Mastery distribution:`)
  logDebug(`      - Unstarted: ${masteryDistribution.unstarted}`)
  logDebug(`      - Struggling (<40): ${masteryDistribution.struggling}`)
  logDebug(`      - Learning (40-85): ${masteryDistribution.learning}`)
  logDebug(
    `      - Mastered (unstable, <5 attempts): ${masteryDistribution.masteredUnstable}`,
  )
  logDebug(
    `      - Mastered (stable, ≥5 attempts): ${masteryDistribution.masteredStable}`,
  )
  if (familiesWithQuestions === 0) {
    logWarn(
      `⚠️ v11.2: Module ${moduleId} has ${eligibleFamilies.length} eligible families for group ${selectedGroup}, but NONE have questions within allowed tiers [${tierRange.min}-${tierRange.max}]`,
    )
    logWarn(
      `   This indicates missing question content. Check seed data for this module.`,
    )
    return []
  }

  // Check if all families with questions are mastered (stable)
  const familiesWithQuestionsAndStableMastery = eligibleFamilies.filter((f) => {
    const hasQuestions = familyQuestionCounts[f.familyId] > 0
    // CRITICAL: Use group-scoped mastery key
    const masteryKey = getFamilyMasteryKey(f.familyId, selectedGroup)
    const mastery = familyMastery[masteryKey]
    return hasQuestions && isMasteryStable(mastery)
  }).length
  const reinforcementMode =
    familiesWithQuestionsAndStableMastery === familiesWithQuestions &&
    familiesWithQuestions > 0
  if (reinforcementMode) {
    logDebug(
      `   ⭐ REINFORCEMENT MODE: All families with questions are stably mastered!`,
    )
  }
  logDebug(
    `   ✅ Pre-flight check passed: ${familiesWithQuestions}/${eligibleFamilies.length} families have questions (${totalQuestionsAvailable} total available)`,
  )

  // v11.2: Session tracking with enhanced metrics
  const session: Question[] = []
  const sessionQuestionIds = new Set<string>()
  const sessionFamilyIds = new Set<string>()
  const sessionFamilyQuestionCounts = new Map<string, number>()
  const recentFamilySet = new Set(
    recentFamilyIds.slice(0, V11_CONFIG.RECENT_FAMILY_COOLDOWN),
  )
  const recentQuestionSet = new Set(recentQuestionIds)
  let masteredCount = 0
  let totalRetries = 0
  let maxFallbackLevel = 0
  const selectionLog: {
    slot: number
    questionId: string | null
    familyId: string | null
    tier: number | null
    reason: string
    fallbackLevel: number
  }[] = []
  logDebug(`\n🔄 Starting question selection (target: ${count} questions)...`)
  logDebug(
    `   🎯 Pedagogical constraints: max ${V11_CONFIG.MAX_QUESTIONS_PER_FAMILY} per family, ${Math.ceil(count * V11_CONFIG.MAX_MASTERED_PERCENTAGE)} max mastered (${Math.round(V11_CONFIG.MAX_MASTERED_PERCENTAGE * 100)}%)`,
  )
  for (let i = 0; i < count; i++) {
    let slotRetries = 0
    let slotFilled = false
    logDebug(`\n   🎯 Slot ${i + 1}/${count}:`)
    logDebug(
      `      - Unique families used: ${sessionFamilyIds.size}/${eligibleFamilies.length}`,
    )
    logDebug(
      `      - Mastered count: ${masteredCount}/${Math.ceil(count * V11_CONFIG.MAX_MASTERED_PERCENTAGE)}`,
    )
    logDebug(`      - Questions selected: ${session.length}`)
    while (
      slotRetries < V11_CONFIG.MAX_RETRIES_PER_SLOT &&
      totalRetries < V11_CONFIG.MAX_TOTAL_RETRIES
    ) {
      const { question, reason, fallbackLevel } = selectAdaptiveQuestionV11(
        moduleId,
        familyMastery,
        selectedGroup,
        allowedTiers,
        sessionFamilyIds,
        sessionQuestionIds,
        recentFamilySet,
        recentQuestionSet,
        masteredCount,
        sessionFamilyQuestionCounts,
        count,
        rng,
      )
      if (!question) {
        logDebug(`      ❌ No question found: ${reason}`)
        selectionLog.push({
          slot: i,
          questionId: null,
          familyId: null,
          tier: null,
          reason,
          fallbackLevel,
        })
        maxFallbackLevel = Math.max(maxFallbackLevel, fallbackLevel)
        break
      }

      // SAFETY CHECK: Verify the selected question is within allowed tiers
      if (!allowedTiers.has(question.tier)) {
        logError(
          `      🚨 TIER VIOLATION: Question ${question.id} has tier ${question.tier} but group ${selectedGroup} allows [${tierRange.min}-${tierRange.max}]. REJECTED.`,
        )
        sessionQuestionIds.add(question.id)
        slotRetries++
        totalRetries++
        continue
      }

      // v11.2: Check per-family cap
      const familyUsage =
        sessionFamilyQuestionCounts.get(question.familyId!) || 0
      if (
        familyUsage >= V11_CONFIG.MAX_QUESTIONS_PER_FAMILY &&
        eligibleFamilies.length >= V11_CONFIG.MIN_FAMILIES_PER_SESSION
      ) {
        logDebug(
          `      ⚠️ Family ${question.familyId} would exceed per-family cap (${familyUsage}/${V11_CONFIG.MAX_QUESTIONS_PER_FAMILY}), retrying...`,
        )
        sessionQuestionIds.add(question.id)
        slotRetries++
        totalRetries++
        continue
      }

      // Valid question — add to session
      session.push(question)
      sessionQuestionIds.add(question.id)
      if (question.familyId) {
        sessionFamilyIds.add(question.familyId)
        sessionFamilyQuestionCounts.set(
          question.familyId,
          (sessionFamilyQuestionCounts.get(question.familyId) || 0) + 1,
        )
      }

      // Track mastered count
      const state = questionStates[question.id]
      if (state?.status === 'mastered') {
        masteredCount++
      }
      maxFallbackLevel = Math.max(maxFallbackLevel, fallbackLevel)
      logDebug(
        `      ✅ Selected: ${question.id} (family: ${question.familyId}, tier: ${question.tier}, fallback: ${fallbackLevel}) - ${reason}`,
      )
      selectionLog.push({
        slot: i,
        questionId: question.id,
        familyId: question.familyId || null,
        tier: question.tier,
        reason,
        fallbackLevel,
      })
      slotFilled = true
      break
    }
    if (!slotFilled) {
      if (totalRetries >= V11_CONFIG.MAX_TOTAL_RETRIES) {
        logWarn(
          `   ⚠️ Hit total retry limit. Generated ${session.length}/${count} questions.`,
        )
      }
      break
    }
  }

  // v11.2: Enhanced session summary with pedagogical metrics
  logDebug(`\n📊 v11.2 Session Generation COMPLETE:`)
  logDebug(`   ✅ Questions generated: ${session.length}/${count}`)
  if (session.length > 0) {
    const tierCounts = new Map<string, number>()
    const familyIds: string[] = []
    const familyUsageCounts = new Map<string, number>()
    let anyOutOfScope = false
    let masteredInSession = 0
    session.forEach((q) => {
      const label = getTierConfig(q.tier)?.levelCode || `T${q.tier}`
      tierCounts.set(label, (tierCounts.get(label) || 0) + 1)
      if (q.familyId) {
        if (!familyIds.includes(q.familyId)) {
          familyIds.push(q.familyId)
        }
        familyUsageCounts.set(
          q.familyId,
          (familyUsageCounts.get(q.familyId) || 0) + 1,
        )
      }
      if (!allowedTiers.has(q.tier)) {
        anyOutOfScope = true
      }
      const state = questionStates[q.id]
      if (state?.status === 'mastered') {
        masteredInSession++
      }
    })
    const summary = Array.from(tierCounts.entries())
      .map(([label, count]) => `${label}:${count}`)
      .join(', ')
    const maxUsage = Math.max(...Array.from(familyUsageCounts.values()))
    const familyUsageSummary = Array.from(familyUsageCounts.entries())
      .map(([fId, count]) => `${fId}:${count}`)
      .join(', ')
    logDebug(`\n   📈 PEDAGOGICAL METRICS:`)
    logDebug(`      - Tier distribution: [${summary}]`)
    logDebug(`      - Unique families used: ${familyIds.length}`)
    logDebug(`      - Family usage: ${familyUsageSummary}`)
    logDebug(
      `      - Max questions per family: ${maxUsage}/${V11_CONFIG.MAX_QUESTIONS_PER_FAMILY}`,
    )
    logDebug(
      `      - Mastered questions: ${masteredInSession}/${session.length} (${Math.round((masteredInSession / session.length) * 100)}%)`,
    )
    logDebug(
      `      - Mastered cap: ${Math.ceil(count * V11_CONFIG.MAX_MASTERED_PERCENTAGE)} (${Math.round(V11_CONFIG.MAX_MASTERED_PERCENTAGE * 100)}%)`,
    )
    logDebug(`      - Reinforcement mode: ${reinforcementMode}`)
    logDebug(`      - Max fallback level reached: ${maxFallbackLevel}`)
    logDebug(
      `      - Tier gating: ${anyOutOfScope ? '❌ VIOLATED' : '✅ STRICT'}`,
    )

    // v11.2: Pedagogical warnings
    if (
      familyIds.length < V11_CONFIG.MIN_FAMILIES_PER_SESSION &&
      eligibleFamilies.length >= V11_CONFIG.MIN_FAMILIES_PER_SESSION
    ) {
      logWarn(
        `   ⚠️ PEDAGOGICAL WARNING: Only ${familyIds.length} families used (recommended: ${V11_CONFIG.MIN_FAMILIES_PER_SESSION}+)`,
      )
    }
    if (maxUsage > V11_CONFIG.MAX_QUESTIONS_PER_FAMILY) {
      logWarn(
        `   ⚠️ PEDAGOGICAL WARNING: Family cap exceeded (${maxUsage}/${V11_CONFIG.MAX_QUESTIONS_PER_FAMILY})`,
      )
    }
    if (
      masteredInSession > Math.ceil(count * V11_CONFIG.MAX_MASTERED_PERCENTAGE)
    ) {
      logWarn(
        `   ⚠️ PEDAGOGICAL WARNING: Mastered cap exceeded (${masteredInSession}/${Math.ceil(count * V11_CONFIG.MAX_MASTERED_PERCENTAGE)})`,
      )
    }
    if (anyOutOfScope) {
      logError(
        `   🚨 CRITICAL: Session contains questions OUTSIDE allowed tiers for group ${selectedGroup}!`,
      )
    }
    logDebug(
      `\n   🎯 Group: ${selectedGroup} | Tiers: [${tierRange.min}-${tierRange.max}]`,
    )
  } else {
    logWarn(
      `   ⚠️ Empty session generated for ${moduleId} in group ${selectedGroup}.`,
    )
    logWarn(
      `   📊 Diagnosis: ${familiesWithQuestions} families have ${totalQuestionsAvailable} questions available, but selection failed.`,
    )
    logWarn(`   🔍 Possible causes:`)
    logWarn(
      `      - All available questions already used in session (need more question variants)`,
    )
    logWarn(`      - Pedagogical constraints too strict for available content`)
    logWarn(`      - All families already selected (need more diverse content)`)
    logWarn(
      `   💡 TIP: Check the selection log below for detailed per-slot diagnostics`,
    )
  }

  // Debug: log selection details
  if (ADAPTIVE_DEBUG && selectionLog.length > 0) {
    group(`🔍 v11.2 Selection Log (${moduleId}, group ${selectedGroup})`)
    selectionLog.forEach((entry) => {
      if (entry.questionId) {
        logDebug(
          `  Slot ${entry.slot + 1}: ${entry.questionId} (family: ${entry.familyId}, tier: ${entry.tier}, fallback: ${entry.fallbackLevel}) — ${entry.reason}`,
        )
      } else {
        logDebug(
          `  Slot ${entry.slot + 1}: NO QUESTION (fallback: ${entry.fallbackLevel}) — ${entry.reason}`,
        )
      }
    })
    groupEnd()
  }
  return session
}

// ============================================================
// DEBUG / QA REPORT
// ============================================================

export interface SelectionDebugReport {
  group: GroupId
  allowedTiers: number[]
  modules: {
    moduleId: ModuleId
    totalFamilies: number
    eligibleFamilies: number
    familiesWithQuestions: number
    missingFamilies: string[]
    tierCoverage: Record<number, number>
  }[]
}

/**
 * Generate a debug report showing content availability per module/group.
 * Use this to identify missing content that would force out-of-scope fallback.
 */
export function generateSelectionDebugReport(
  group: GroupId,
  moduleIds: ModuleId[],
): SelectionDebugReport {
  const allowedTiers = getAllowedTiersForGroup(group)
  const allowedArray = Array.from(allowedTiers).sort((a, b) => a - b)
  const modules = moduleIds.map((moduleId) => {
    const allFamilies = getFamiliesForModule(moduleId)
    const eligibleFamilies = allFamilies.filter((f) => allowedTiers.has(f.tier))
    const tierCoverage: Record<number, number> = {}
    const missingFamilies: string[] = []
    let familiesWithQuestions = 0
    for (const family of eligibleFamilies) {
      const questions = QUESTIONS_BY_FAMILY.get(family.familyId) || []
      const inTierQuestions = questions.filter((q) => allowedTiers.has(q.tier))
      if (inTierQuestions.length > 0) {
        familiesWithQuestions++
        inTierQuestions.forEach((q) => {
          tierCoverage[q.tier] = (tierCoverage[q.tier] || 0) + 1
        })
      } else {
        missingFamilies.push(family.familyId)
      }
    }
    return {
      moduleId,
      totalFamilies: allFamilies.length,
      eligibleFamilies: eligibleFamilies.length,
      familiesWithQuestions,
      missingFamilies,
      tierCoverage,
    }
  })
  return {
    group,
    allowedTiers: allowedArray,
    modules,
  }
}

// ============================================================
// LEVEL PROGRESSION (Tier-Based)
// ============================================================

export interface TierProgressInfo {
  tiers: TierConfig[]
  currentTier: number
  tierDetails: {
    tier: number
    label: string
    title: string
    isUnlocked: boolean
    isCurrent: boolean
    isCompleted: boolean
    totalQuestions: number
    attemptedCount: number
    practicedOrMasteredCount: number
    progressPercent: number
    group: number
  }[]
  fastTrackActive: boolean
}

/**
 * Compute tier progress for display.
 * Replaces computeLevelProgress.
 */
export function computeTierProgress(
  moduleId: ModuleId,
  group: GroupId,
  questionStates: Record<string, QuestionState>,
  startingTier: number,
): TierProgressInfo {
  const groupTiers = getTiersForGroup(group)
  const effectiveStartingTier = startingTier || groupTiers[0]?.tier || 1

  // Show tiers for current group + small lookahead
  const currentGroupEnd = groupTiers[groupTiers.length - 1]?.tier || 18
  const nextGroupStart =
    group < 8 ? getTiersForGroup((group + 1) as GroupId) : []
  const lookaheadTiers = new Set(nextGroupStart.slice(0, 2).map((t) => t.tier))
  const visibleTiers = TIERS.filter(
    (t) =>
      (t.tier >= effectiveStartingTier && t.tier <= currentGroupEnd) ||
      lookaheadTiers.has(t.tier),
  )

  // Compute unlocked tiers (simplified for v11: based on previous tier completion)
  // In v11, we assume linear progression for the visualization, even if adaptive engine jumps around
  const unlockedTiers = new Set<number>()
  unlockedTiers.add(effectiveStartingTier)

  // Also unlock any tier where the user has practiced questions
  // This handles cases where adaptive engine jumped ahead
  Object.values(questionStates).forEach((state) => {
    // We need to look up the question to get its tier
    // This is expensive if we do it for all states.
    // Optimization: We can iterate visible tiers and check their questions
  })
  const tierDetails = visibleTiers.map((tierConfig) => {
    const questionsAtTier = getQuestionsForTier(moduleId, tierConfig.tier)
    let attemptedCount = 0
    let practicedOrMasteredCount = 0
    questionsAtTier.forEach((q) => {
      const state = questionStates[q.id]
      if (state && state.attempts > 0) {
        attemptedCount++
        if (state.status === 'practiced' || state.status === 'mastered') {
          practicedOrMasteredCount++
        }
      }
    })
    const totalQuestions = questionsAtTier.length

    // Completion logic
    const isCompleted =
      totalQuestions > 0 &&
      practicedOrMasteredCount >=
        Math.ceil(totalQuestions * V11_CONFIG.LEVEL_UNLOCK_THRESHOLD) &&
      attemptedCount >= V11_CONFIG.LEVEL_UNLOCK_MIN_ATTEMPTED

    // Unlock next tier if this one is completed
    if (isCompleted) {
      unlockedTiers.add(tierConfig.tier + 1)
    }

    // Also unlock if we have significant progress (e.g. started working on it)
    if (attemptedCount > 0) {
      unlockedTiers.add(tierConfig.tier)
    }
    const progressPercent =
      totalQuestions > 0
        ? Math.round((practicedOrMasteredCount / totalQuestions) * 100)
        : 0
    return {
      tier: tierConfig.tier,
      label: tierConfig.levelCode,
      title: tierConfig.title,
      isUnlocked: false,
      // Set in second pass
      isCurrent: false,
      // Set later
      isCompleted,
      totalQuestions,
      attemptedCount,
      practicedOrMasteredCount,
      progressPercent,
      group: tierConfig.group,
    }
  })

  // Second pass to set isUnlocked based on the set we built
  tierDetails.forEach((d) => {
    d.isUnlocked = unlockedTiers.has(d.tier)
  })

  // Determine current tier (highest unlocked and not completed)
  let currentTier = effectiveStartingTier
  for (let i = tierDetails.length - 1; i >= 0; i--) {
    const detail = tierDetails[i]
    if (detail.isUnlocked && !detail.isCompleted && detail.totalQuestions > 0) {
      currentTier = detail.tier
      break
    }
  }

  // If all unlocked are completed, move to next
  const allUnlockedCompleted = tierDetails
    .filter((d) => d.isUnlocked && d.totalQuestions > 0)
    .every((d) => d.isCompleted)
  if (allUnlockedCompleted) {
    // Find the highest unlocked tier
    let maxUnlocked = 0
    tierDetails.forEach((d) => {
      if (d.isUnlocked) maxUnlocked = Math.max(maxUnlocked, d.tier)
    })
    // If there's a next tier in the list, make it current
    const next = tierDetails.find((d) => d.tier === maxUnlocked + 1)
    if (next) currentTier = next.tier
    else currentTier = maxUnlocked
  }
  tierDetails.forEach((d) => {
    d.isCurrent = d.tier === currentTier
  })
  return {
    tiers: visibleTiers,
    currentTier,
    tierDetails,
    fastTrackActive: false, // Deprecated in v11
  }
}

// ============================================================
// SUBSKILL MASTERY (Preserved for UI)
// ============================================================

export function computeSubSkillMastery(
  moduleId: ModuleId,
  subSkill: string,
  questionStates: Record<string, QuestionState>,
  group?: GroupId,
  familyMastery?: Record<string, FamilyMasteryState>, // NEW: Optional for backward compat, but preferred
): SubSkillProgress {
  let relevantQuestions = getModuleSubSkillQuestions(moduleId, subSkill)

  // Apply group filter if needed
  if (group !== undefined) {
    relevantQuestions = relevantQuestions.filter((q) => q.group === group)
  }

  if (relevantQuestions.length === 0) {
    return {
      subSkill,
      masteryScore: 0,
      questionsAnswered: 0,
      correctCount: 0,
      trend: 'stable',
      lastPracticed: 0,
    }
  }

  // 1. Calculate attempt stats from questionStates (still needed for counts/trend)
  let totalAttempts = 0
  let correctAttempts = 0
  let lastPracticed = 0
  const allAttempts: {
    isCorrect: boolean
    timestamp: number
  }[] = []

  relevantQuestions.forEach((q) => {
    // Use group-scoped key to look up state
    const stateKey =
      group !== undefined ? getQuestionStateKey(q.id, group) : q.id
    const state = questionStates[stateKey]
    if (state && state.attempts > 0) {
      totalAttempts += state.attempts
      correctAttempts += state.correctAttempts
      lastPracticed = Math.max(lastPracticed, state.lastAttemptDate)
      allAttempts.push(...state.history)
    }
  })

  // 2. Calculate mastery score
  let finalScore = 0

  if (familyMastery && group !== undefined) {
    // CANONICAL PATH: Use family mastery if available
    // Find unique families for this subSkill
    const familyIds = new Set<string>()
    relevantQuestions.forEach((q) => {
      if (q.familyId) familyIds.add(q.familyId)
    })

    if (familyIds.size > 0) {
      let totalFamilyScore = 0
      let attemptedFamilies = 0

      familyIds.forEach((familyId) => {
        const key = getFamilyMasteryKey(familyId, group)
        const state = familyMastery[key]
        if (state && state.attempts > 0) {
          totalFamilyScore += state.masteryScore
          attemptedFamilies++
        }
      })

      if (attemptedFamilies > 0) {
        finalScore = Math.round(totalFamilyScore / attemptedFamilies)
      }
    }
  } else {
    // FALLBACK PATH: Use question attempt history (legacy logic)
    allAttempts.sort((a, b) => b.timestamp - a.timestamp)
    const recentWindow = allAttempts.slice(0, 8) // Hardcoded 8 from config

    if (recentWindow.length > 0) {
      let weightedScore = 0
      let totalWeight = 0
      recentWindow.forEach((attempt, index) => {
        const weight = 1 / (index + 1)
        weightedScore += (attempt.isCorrect ? 100 : 0) * weight
        totalWeight += weight
      })
      finalScore = Math.round(weightedScore / totalWeight)

      // Volume penalty
      if (totalAttempts < 5) {
        const volumeMultiplier = totalAttempts / 5
        finalScore = Math.round(finalScore * volumeMultiplier)
      }
    }
  }

  // 3. Calculate trend
  let trend: 'improving' | 'stable' | 'struggling' = 'stable'
  allAttempts.sort((a, b) => b.timestamp - a.timestamp) // Ensure sorted
  const recentWindow = allAttempts.slice(0, 8)

  if (recentWindow.length >= 4) {
    const recentHalf = recentWindow.slice(
      0,
      Math.floor(recentWindow.length / 2),
    )
    const olderHalf = recentWindow.slice(Math.floor(recentWindow.length / 2))

    if (recentHalf.length > 0 && olderHalf.length > 0) {
      const recentAvg =
        recentHalf.filter((a) => a.isCorrect).length / recentHalf.length
      const olderAvg =
        olderHalf.filter((a) => a.isCorrect).length / olderHalf.length
      if (recentAvg > olderAvg + 0.2) trend = 'improving'
      else if (recentAvg < olderAvg - 0.2) trend = 'struggling'
    }
  }

  return {
    subSkill,
    masteryScore: finalScore,
    questionsAnswered: totalAttempts,
    correctCount: correctAttempts,
    trend,
    lastPracticed,
  }
}
